import { useCampaignEditorContext } from 'sections/onsite-campaigns/pages/CampaignEditor/contexts/CampaignEditorContext';
import { ExitIntentAreas, OnClickConfig, TriggersConfigMap, TriggerType } from 'sections/onsite-campaigns/types';
import { useFeatureFlags } from 'core/app/AppContext';

export const useMessageTriggers = (messageId: string) => {
  const campaignContext = useCampaignEditorContext();
  const { flagEnabled } = useFeatureFlags();
  const message = campaignContext?.state?.edit?.messages?.[messageId] || campaignContext?.state?.messages?.[messageId];

  const newTriggersEnabled = flagEnabled('ld-new-variant-onsite-triggers');

  const defaultTriggers: TriggersConfigMap = {
    [TriggerType.Inactivity]: { active: false, value: 0 },
    [TriggerType.ExitIntent]: { active: false, value: [] as ExitIntentAreas[] },
    ...(newTriggersEnabled && {
      [TriggerType.PageLoad]: { active: false, value: false },
      [TriggerType.OnClick]: { active: false, value: { selector: '', overrideFreqCaps: true } as OnClickConfig },
    }),
  };

  const mergedTriggers: TriggersConfigMap | undefined = message
    ? { ...defaultTriggers, ...(message.triggers || {}) }
    : undefined;

  const setTrigger = <T extends keyof TriggersConfigMap>(triggerType: T, trigger: TriggersConfigMap[T]) => {
    const updatedTriggers = { ...message.triggers, [triggerType]: trigger };

    if (triggerType === TriggerType.PageLoad && trigger.active) {
      // When PageLoad is selected, deselect all other triggers
      Object.keys(defaultTriggers).forEach(otherTriggerType => {
        if (otherTriggerType !== TriggerType.PageLoad) {
          const otherTrigger = otherTriggerType as keyof TriggersConfigMap;
          updatedTriggers[otherTrigger] = {
            active: false,
            value: otherTrigger === TriggerType.Inactivity ? 0 : [],
          };
        }
      });
    } else if (triggerType !== TriggerType.PageLoad && trigger.active && mergedTriggers?.[TriggerType.PageLoad]) {
      // When any other trigger is selected, deselect PageLoad (if it exists)
      updatedTriggers[TriggerType.PageLoad] = {
        active: false,
        value: false,
      };
    }

    campaignContext.dispatch({
      type: 'UPDATE_MESSAGE_TRIGGERS',
      payload: {
        id: message.id,
        triggers: updatedTriggers,
      },
    });
  };

  return { triggers: mergedTriggers, setTrigger };
};
